import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiDelete, apiGet, apiPost, apiPut } from "../../../service/apiService";
import axios from "axios";
import { baseUrl } from "../../../service/apiService";

const initialState = {
    loading: false,
    appliedExamsList: null,
    jotUsersList: null,
    getUserExamDetailsById: null,
    userExamDetailsUpdate: null,
    getUserExamDetailsPdf: null,
    userExamDetailsDelete: null,
    refreshingResultStatus: false
};

export const appliedExamsListGet = createAsyncThunk(
    'jot/appliedExamsListGet',
    async (params, { rejectWithValue }) => {
        try {
            const { page, search, filter, exam_level, result_type } = params;
            const response = await apiGet(`jot/applied/exams?page=${page}&search=${search}&filter=${filter}&exam_level=${exam_level}&result_type=${result_type}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);


export const jotUsersListGet = createAsyncThunk(
    'jot/jotUsersListGet',
    async (params, { rejectWithValue }) => {
        try {
            const { page, search, payment_status } = params;
            const response = await apiGet('jot/users', {
                page,
                search,
                payment_status
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);


export const userExamDetailsGetById = createAsyncThunk(
    'jot/userExamDetailsGetById',
    async (params, { rejectWithValue }) => {
        const {id, applied_date, application_id} = params
        try {
            const response = await apiGet(`jot/applied/exams/${id}/${applied_date}`, application_id ? { application_id } : undefined);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const userExamDetailsUpdate = createAsyncThunk(
    'jot/userExamDetailsUpdate',
    async ({id, data}, { rejectWithValue }) => {
        try {
            const response = await apiPut(`jot/exam/${id}`, data);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);


export const userExamDetailsDelete = createAsyncThunk(
    'jot/userExamDetailsDelete',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiDelete(`jot/exam/${id}`);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const userExamDetailsPdfGetById = createAsyncThunk(
    'jot/userExamDetailsPdfGetById',
    async (params, { rejectWithValue }) => {
        const {id, applied_date} = params
        try {
            const response = await axios.get(`${baseUrl}jot/generate_pdf/${id}/${applied_date}`, {
          responseType: 'arraybuffer', 
        });
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const refreshExamResultStatus = createAsyncThunk(
    'jot/refreshExamResultStatus',
    async (applicationId, { rejectWithValue }) => {
        try {
            const response = await apiPost('partner/payment-status/refresh', {
                application_id: applicationId
            });
            // apiPost swallows errors and returns the error response, so check the status here
            if (response?.status !== 200) {
                return rejectWithValue(
                    response?.data?.error ||
                    response?.data?.message ||
                    'Unable to refresh result status.'
                );
            }
            return response.data;
        } catch (error) {
            return rejectWithValue('Unable to refresh result status.');
        }
    }
);


// Typed correction (partner policy sections 5.4, 5.6 and 6).
export const submitExamCorrection = createAsyncThunk(
    'jot/submitExamCorrection',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await apiPut(`jot/exam/${id}/correction`, data);
            // apiPut returns the error response instead of throwing
            if (response?.status !== 200) {
                return rejectWithValue(response?.data?.message || 'Unable to submit the correction.');
            }
            return response.data;
        } catch (error) {
            return rejectWithValue('Unable to submit the correction.');
        }
    }
);

// Retry the last JOT push (partner policy section 9).
export const resyncExam = createAsyncThunk(
    'jot/resyncExam',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiPost(`jot/exam/${id}/resync`, {});
            if (response?.status !== 200) {
                return rejectWithValue(response?.data?.message || 'Unable to send to JOT.');
            }
            return response.data;
        } catch (error) {
            return rejectWithValue('Unable to send to JOT.');
        }
    }
);


const JotSlice = createSlice({
    name: "JotSlice",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(appliedExamsListGet.pending, (state) => {
                state.loading = true;
            })
            .addCase(appliedExamsListGet.fulfilled, (state, action) => {
                state.loading = false;
                state.appliedExamsList = action.payload;
            })
            .addCase(appliedExamsListGet.rejected, (state) => {
                state.loading = false;
            })

            .addCase(jotUsersListGet.pending, (state) => {
                state.loading = true;
            })
            .addCase(jotUsersListGet.fulfilled, (state, action) => {
                state.loading = false;
                state.jotUsersList = action.payload;
            })
            .addCase(jotUsersListGet.rejected, (state) => {
                state.loading = false;
            })

            .addCase(userExamDetailsGetById.pending, (state) => {
                state.loading = true;
            })
            .addCase(userExamDetailsGetById.fulfilled, (state, action) => {
                state.loading = false
                state.getUserExamDetailsById = action.payload
            })
            .addCase(userExamDetailsGetById.rejected, (state) => {
                state.loading = false;
            })

            .addCase(userExamDetailsUpdate.pending, (state) => {
                state.loading = true
            })
            .addCase(userExamDetailsUpdate.fulfilled, (state, action) => {
                state.loading = false;
                state.userExamDetailsUpdate = action.payload
            })
            .addCase(userExamDetailsUpdate.rejected, (state) => {
                state.loading = false;
            })

            .addCase(userExamDetailsDelete.pending, (state) => {
                state.loading = true
            })
            .addCase(userExamDetailsDelete.fulfilled, (state, action) => {
                state.loading = false;
                state.userExamDetailsDelete = action.payload
            })
            .addCase(userExamDetailsDelete.rejected, (state) => {
                state.loading = false;
            })

            .addCase(userExamDetailsPdfGetById.pending, (state) => {
                state.loading = true;
            })
            .addCase(userExamDetailsPdfGetById.fulfilled, (state, action) => {
                state.loading = false
                state.getUserExamDetailsPdf = action.payload
            })
            .addCase(userExamDetailsPdfGetById.rejected, (state) => {
                state.loading = false;
            })

            .addCase(refreshExamResultStatus.pending, (state) => {
                state.refreshingResultStatus = true;
            })
            .addCase(refreshExamResultStatus.fulfilled, (state) => {
                state.refreshingResultStatus = false;
            })
            .addCase(refreshExamResultStatus.rejected, (state) => {
                state.refreshingResultStatus = false;
            })
    }
})

export default JotSlice.reducer

